import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ZoomIn, ChevronLeft, ChevronRight, Filter, Play, Download } from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState('todos');
  const [isZoomed, setIsZoomed] = useState(false);

  // Categorías de proyectos
  const categories = [
    { id: 'todos', label: 'Todos los Proyectos' },
    { id: 'apps', label: 'Aplicaciones Móviles' },
    { id: 'sistemas', label: 'Sistemas Empresariales' },
    { id: 'apis', label: 'APIs y Backends' },
    { id: 'contabilidad', label: 'Soluciones Contables' }
  ];

  // Imágenes con categorías y descripciones detalladas
  const images = [
    { 
      src: '/images/sistema/gastro1.png', 
      alt: 'Sistema de Gestión para Restaurantes', 
      category: 'sistemas',
      title: 'GastroManager Pro',
      description: 'Sistema completo de gestión para restaurantes con control de inventario, mesas y pedidos.',
      technologies: ['React', 'Node.js', 'MongoDB'],
      year: '2024'
    },
    { 
      src: '/images/sistema/gastro2.png', 
      alt: 'Panel de Control Restaurantes', 
      category: 'sistemas',
      title: 'Dashboard GastroManager',
      description: 'Panel de control en tiempo real para gestión de establecimientos gastronómicos.',
      technologies: ['React', 'Socket.io', 'Chart.js'],
      year: '2024'
    },
    { 
      src: '/images/sistema/gastro3.png', 
      alt: 'App Móvil para Restaurantes', 
      category: 'apps',
      title: 'GastroManager Mobile',
      description: 'Aplicación móvil para gestión de pedidos y control de mesas desde dispositivos móviles.',
      technologies: ['React Native', 'Firebase'],
      year: '2024'
    },
    { 
      src: '/images/sistema/wasapi.png', 
      alt: 'Plataforma Wasapi', 
      category: 'sistemas',
      title: 'Wasapi Platform',
      description: 'Plataforma integral de automatización de marketing y comunicación empresarial.',
      technologies: ['Vue.js', 'Laravel', 'MySQL'],
      year: '2023'
    },
    { 
      src: '/images/sistema/wasapi0.jpeg', 
      alt: 'Panel de Control Wasapi', 
      category: 'sistemas',
      title: 'Dashboard Wasapi',
      description: 'Panel administrativo con métricas y control de campañas de marketing.',
      technologies: ['Vue.js', 'Laravel', 'Redis'],
      year: '2023'
    },
    { 
      src: '/images/sistema/wasapi2.png', 
      alt: 'Automatización Wasapi', 
      category: 'sistemas',
      title: 'Módulo de Automatización',
      description: 'Sistema de automatización de flujos de trabajo y comunicaciones.',
      technologies: ['Node.js', 'WebSocket', 'PostgreSQL'],
      year: '2023'
    },
    { 
      src: '/images/sistema/app_wasapi2.jpeg', 
      alt: 'APP Wasapi Móvil', 
      category: 'apps',
      title: 'Wasapi Mobile App',
      description: 'Aplicación móvil para gestión de campañas y comunicación en tiempo real.',
      technologies: ['React Native', 'Firebase'],
      year: '2023'
    },
    { 
      src: '/images/sistema/app_wasapi3.jpeg', 
      alt: 'APP Wasapi UI', 
      category: 'apps',
      title: 'Interfaz Wasapi Mobile',
      description: 'Diseño intuitivo y moderno para la aplicación móvil de Wasapi.',
      technologies: ['React Native', 'Styled Components'],
      year: '2023'
    },
    { 
      src: '/images/sistema/app_wasapi1.jpeg', 
      alt: 'APP Wasapi Funcionalidades', 
      category: 'apps',
      title: 'Wasapp Features',
      description: 'Pantalla principal con todas las funcionalidades de la aplicación móvil.',
      technologies: ['React Native', 'Redux'],
      year: '2023'
    },
    { 
      src: '/images/sistema/panel_api.png', 
      alt: 'Visioner7 API', 
      category: 'apis',
      title: 'Visioner7 API Gateway',
      description: 'Panel de administración para gestión de APIs y microservicios.',
      technologies: ['React', 'Express.js', 'MongoDB'],
      year: '2024'
    },
    { 
      src: '/images/sistema/panel_api1.png', 
      alt: 'API Management', 
      category: 'apis',
      title: 'API Management System',
      description: 'Sistema completo de gestión y monitoreo de APIs empresariales.',
      technologies: ['Node.js', 'Express', 'JWT'],
      year: '2024'
    },
    { 
      src: '/images/sistema/panel_api2.png', 
      alt: 'API Dashboard', 
      category: 'apis',
      title: 'API Analytics Dashboard',
      description: 'Dashboard con métricas y análisis del rendimiento de APIs.',
      technologies: ['React', 'Chart.js', 'REST API'],
      year: '2024'
    },
    { 
      src: '/images/sistema/app_factus1.jpeg', 
      alt: 'APP Factus', 
      category: 'contabilidad',
      title: 'Factus Mobile App',
      description: 'Aplicación móvil para gestión contable y facturación electrónica.',
      technologies: ['Flutter', 'Dart', 'SQLite'],
      year: '2023'
    },
    { 
      src: '/images/sistema/app_factus2.jpeg', 
      alt: 'APP Factus UI', 
      category: 'contabilidad',
      title: 'Factus User Interface',
      description: 'Interfaz moderna y funcional para la aplicación de contabilidad Factus.',
      technologies: ['Flutter', 'Material Design'],
      year: '2023'
    },
    { 
      src: '/images/sistema/transport.png', 
      alt: 'Sistema de Transporte', 
      category: 'sistemas',
      title: 'Transport Management System',
      description: 'Sistema de gestión para empresas de transporte de pasajeros.',
      technologies: ['Angular', 'Spring Boot', 'PostgreSQL'],
      year: '2023'
    }
  ];

  // Filtrar imágenes por categoría
  const filteredImages = activeFilter === 'todos' 
    ? images 
    : images.filter(img => img.category === activeFilter);

  // Navegación del modal
  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredImages.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  // Manejar teclado en el modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImage) {
        if (e.key === 'Escape') setSelectedImage(null);
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentIndex]);

  // Abrir imagen en modal
  const openImage = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    setIsZoomed(false);
  };

  return (
    <section id="galeria" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Encabezado */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full text-blue-600 font-semibold text-sm mb-4"
          >
            Portfolio de Trabajos
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Nuestra <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Galería</span> de Proyectos
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Descubre los proyectos que han transformado negocios y creado impacto real en la industria.
          </p>
        </motion.div>

        {/* Filtros */}
        <motion.div
          className="flex flex-wrap justify-center gap-4 mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {categories.map((category) => (
            <motion.button
              key={category.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveFilter(category.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeFilter === category.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
              }`}
            >
              {category.label}
            </motion.button>
          ))}
        </motion.div>

        {/* Contador de proyectos */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-gray-600">
            Mostrando <span className="font-bold text-blue-600">{filteredImages.length}</span> de{' '}
            <span className="font-bold text-gray-900">{images.length}</span> proyectos
          </p>
        </motion.div>

        {/* Grid de imágenes */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          layout
        >
          <AnimatePresence>
            {filteredImages.map((image, index) => (
              <motion.div
                key={`${image.src}-${index}`}
                layout
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="group relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer"
                onClick={() => openImage(image, index)}
              >
                {/* Imagen */}
                <div className="relative overflow-hidden">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  
                  {/* Overlay de hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-6">
                    <div>
                      <h3 className="text-white font-bold text-lg mb-2">{image.title}</h3>
                      <p className="text-gray-200 text-sm">{image.description}</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <ZoomIn className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Badge de categoría */}
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {image.category}
                  </div>

                  {/* Badge de año */}
                  <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded-full text-xs">
                    {image.year}
                  </div>
                </div>

                {/* Información de la tarjeta */}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-gray-900 text-lg">{image.title}</h3>
                    <span className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full">
                      {image.year}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{image.description}</p>
                  
                  {/* Tecnologías */}
                  <div className="flex flex-wrap gap-2">
                    {image.technologies.map((tech, i) => (
                      <span key={i} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Mensaje si no hay proyectos en la categoría */}
        {filteredImages.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="text-6xl mb-4">📷</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No hay proyectos en esta categoría</h3>
            <p className="text-gray-600">Prueba con otra categoría para ver más proyectos.</p>
          </motion.div>
        )}
      </div>

      {/* Modal de imagen */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-6xl max-h-full bg-black rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Controles del modal */}
              <div className="absolute top-4 right-4 z-20 flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsZoomed(!isZoomed)}
                  className="bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-colors"
                >
                  <ZoomIn className="w-5 h-5" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelectedImage(null)}
                  className="bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Navegación */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-colors z-20"
              >
                <ChevronLeft className="w-6 h-6" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-colors z-20"
              >
                <ChevronRight className="w-6 h-6" />
              </motion.button>

              {/* Imagen */}
              <motion.img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className={`w-full h-auto max-h-[80vh] object-contain cursor-${isZoomed ? 'zoom-out' : 'zoom-in'}`}
                animate={{ scale: isZoomed ? 1.5 : 1 }}
                transition={{ duration: 0.3 }}
                onClick={() => setIsZoomed(!isZoomed)}
              />

              {/* Información de la imagen */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-8 text-white">
                <h3 className="text-2xl font-bold mb-2">{selectedImage.title}</h3>
                <p className="text-gray-200 mb-4">{selectedImage.description}</p>
                <div className="flex flex-wrap gap-2">
                  {selectedImage.technologies.map((tech, i) => (
                    <span key={i} className="bg-white/20 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-gray-300">{currentIndex + 1} / {filteredImages.length}</span>
                  <span className="text-gray-300">{selectedImage.year}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;